// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat3_iloczynowa_types/FindAX1X2FromFactoredTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* =============================================================
   TASK: FindAX1X2FromFactoredTask
   Cel: odczytaj a, x1, x2 z f(x) = a(x - x1)(x - x2)
   Input: FractionInput użyty 3× (a, x1, x2); kolejność pierwiastków dowolna
============================================================= */

/* ---------- helpers ---------- */
function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}
function gcd(a, b){ a=Math.abs(a); b=Math.abs(b); while(b){const t=a%b;a=b;b=t;} return a||1; }
function simp(n, d){
  if (d === 0) return { n, d };
  const s = Math.sign(n)*Math.sign(d) || 1;
  const an = Math.abs(n), ad = Math.abs(d);
  const g = gcd(an, ad);
  return { n: s*(an/g), d: ad/g };
}
function formatFracMixed(n, d){
  const sgn = Math.sign(n) < 0 ? "-" : "";
  const N = Math.abs(n), D = Math.abs(d);
  if (N % D === 0) return `${sgn}${N/D}`;
  const whole = Math.floor(N/D);
  const rem = N % D;
  if (whole === 0) return `${sgn}\\dfrac{${N}}{${D}}`;
  return `${sgn}${whole}\\dfrac{${rem}}{${D}}`;
}
function parseFractionLike(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/\u2212/g, "-").replace(",", ".");
  // liczba mieszana: A B/C
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = parseInt(m[1],10), B = parseInt(m[2],10), C = parseInt(m[3],10);
    if (!C) return null;
    const frac = B/C;
    return A >= 0 ? (A+frac) : (A-frac);
  }
  // zwykły ułamek P/Q
  m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const P = parseInt(m[1],10), Q = parseInt(m[2],10);
    if (!Q) return null;
    return P/Q;
  }
  // całkowita / dziesiętna
  if (/^[+-]?\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}
function prettyRawToLatex(raw) {
  if (raw == null || String(raw).trim() === "") return "?";
  let s = String(raw).trim().replace(/\u2212/g, "-").replace(",", ".");
  // liczba mieszana
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) return `${m[1]}\\;\\dfrac{${m[2]}}{${m[3]}}`;
  // zwykły ułamek
  m = s.match(/^\s*([+-]?\d+)\\?\/\\?(\d+)\s*$/);
  if (m) return `\\dfrac{${m[1]}}{${m[2]}}`;
  // dziesiętna -> przecinek
  if (/^[+-]?\d+\.\d+$/.test(s)) return s.replace(".", "{,}");
  return s;
}

/* ---------- latex budowanie postaci iloczynowej ---------- */
function factorOf(xFrac){ // „(x - r)” z ładnym r
  const r = formatFracMixed(xFrac.n, xFrac.d);
  const isNeg = xFrac.n < 0;
  const absR = formatFracMixed(Math.abs(xFrac.n), xFrac.d);
  return isNeg ? `\\big(x + ${absR}\\big)` : `\\big(x - ${r}\\big)`;
}
function factoredLatex(a, x1Frac, x2Frac) {
  const aStr = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  return `${aStr}${factorOf(x1Frac)}${factorOf(x2Frac)}`;
}

/* ---------- generator ---------- */
export function generateTask() {
  // a ≠ 0, x1 ≠ x2; generujemy też czasem ułamki o małych mianownikach
  const a = randInt(-5, 5, new Set([0]));
  const pickDen = () => randInt(1, 6); // 1..6 (1 => liczby całkowite)
  function randFrac() {
    let n = randInt(-9, 9, new Set([0]));
    let d = pickDen();
    const s = simp(n, d);
    return s;
  }
  let x1 = randFrac();
  let x2 = randFrac();
  // dopilnuj różności
  while (x1.n * x2.d === x2.n * x1.d) x2 = randFrac();

  return {
    type: "FindAX1X2FromFactoredTask",
    data: {
      a,
      x1Frac: x1,
      x2Frac: x2,
      x1: x1.n / x1.d,
      x2: x2.n / x2.d,
    }
  };
}

/* ---------- render ---------- */
export function renderTask({ data }) {
  const { a, x1Frac, x2Frac } = data;
  const body = factoredLatex(a, x1Frac, x2Frac);
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci iloczynowej:{" "}
        <InlineMath math={`f(x) = ${body}`} />
      </div>
      <div>
        Odczytaj <InlineMath math={'a'} />,{" "}
        <InlineMath math={'x_1'} /> oraz <InlineMath math={'x_2'} />.
      </div>
    </div>
  );
}

/* ---------- input ---------- */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { a:{raw:""}, x1:{raw:""}, x2:{raw:""} };
  const group = { display: "grid", gap: 6 };

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
      <div style={group}>
        <InlineMath math={'a ='} />
        <FractionInput
          value={val.a}
          onChange={(v)=>onChange({ ...(userAnswer??{}), a: v })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div style={group}>
        <InlineMath math={'x_1 ='} />
        <FractionInput
          value={val.x1}
          onChange={(v)=>onChange({ ...(userAnswer??{}), x1: v })}
          disabled={disabled}
          label="x_1"
        />
      </div>
      <div style={group}>
        <InlineMath math={'x_2 ='} />
        <FractionInput
          value={val.x2}
          onChange={(v)=>onChange({ ...(userAnswer??{}), x2: v })}
          disabled={disabled}
          label="x_2"
        />
      </div>
    </div>
  );
}

/* ---------- check ---------- */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const aRaw  = userAnswer?.a ?.raw ?? userAnswer?.a  ?? "";
  const x1Raw = userAnswer?.x1?.raw ?? userAnswer?.x1 ?? "";
  const x2Raw = userAnswer?.x2?.raw ?? userAnswer?.x2 ?? "";

  const aParsed  = parseFractionLike(aRaw);
  const x1Parsed = parseFractionLike(x1Raw);
  const x2Parsed = parseFractionLike(x2Raw);

  if ([aParsed,x1Parsed,x2Parsed].some(v => v == null || !Number.isFinite(v))) return false;

  const usedDec = (s) => /[.,]\d/.test(String(s ?? ""));
  const tolA  = usedDec(aRaw)  ? 1e-2 : 1e-9;
  const tolX1 = usedDec(x1Raw) ? 1e-2 : 1e-9;
  const tolX2 = usedDec(x2Raw) ? 1e-2 : 1e-9;

  const okA = Math.abs(aParsed - data.a) < tolA;

  // x1,x2 — kolejność dowolna
  const target = [data.x1, data.x2].sort((u,v)=>u-v);
  const given  = [x1Parsed, x2Parsed].sort((u,v)=>u-v);
  const okX =
    Math.abs(given[0] - target[0]) < Math.max(tolX1, tolX2) &&
    Math.abs(given[1] - target[1]) < Math.max(tolX1, tolX2);

  return okA && okX;
}

/* ---------- answer ---------- */
export function renderAnswer({ data }) {
  const a = data.a;
  const x1 = formatFracMixed(data.x1Frac.n, data.x1Frac.d);
  const x2 = formatFracMixed(data.x2Frac.n, data.x2Frac.d);
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`a = ${a},\\quad x_{1} = ${x1},\\quad x_{2} = ${x2}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const body = factoredLatex(data.a, data.x1Frac, data.x2Frac);
  const note = (
    <div style={{ opacity: 0.9, marginTop: "0.4rem" }}>
      W nawiasie znajduje się wyrażenie <InlineMath math={'(x - r)'} />.
      Jeśli natomiast widzisz <InlineMath math={'(x + t)'} />, to oznacza to, że
      <InlineMath math={'r = -t'} /> — czyli pierwiastek jest przeciwny
      do znaku stojącego w nawiasie.
    </div>
  );

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Porównaj z definicją postaci iloczynowej:</div>
      <BlockMath math={'f(x)=a\\,(x-x_1)\\,(x-x_2)'} />
      <div><strong>Krok 2.</strong> Podana funkcja:</div>
      <BlockMath math={`f(x) = ${body}`} />
      {note}
      <div><strong>Krok 3.</strong> Odczytujemy:</div>
      <BlockMath math={`a=${data.a},\\quad x_1=${formatFracMixed(data.x1Frac.n,data.x1Frac.d)},\\quad x_2=${formatFracMixed(data.x2Frac.n,data.x2Frac.d)}`} />
    </div>
  );
}

/* ---------- user answer (ładny podgląd) ---------- */
export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data;
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  const raw = {
    a:  userAnswer && (userAnswer.a ?.raw ?? userAnswer.a ) || "",
    x1: userAnswer && (userAnswer.x1?.raw ?? userAnswer.x1) || "",
    x2: userAnswer && (userAnswer.x2?.raw ?? userAnswer.x2) || "",
  };
  const empty = (s) => !String(s ?? "").trim();
  if (empty(raw.a) && empty(raw.x1) && empty(raw.x2)) return <i>(brak odpowiedzi)</i>;

  const show = (r) => {
    const s = String(r ?? "").trim();
    if (!s) return "?";
    return prettyRawToLatex(r);
    };

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`a = ${show(raw.a)}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`x_{1} = ${show(raw.x1)}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`x_{2} = ${show(raw.x2)}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
